/**
 * Cross-origin OPFS manager.
 *
 * OPFS data lives on the Playground origin. This class creates a hidden
 * iframe pointing to the Playground's opfs-manager.html page and wraps
 * postMessage communication in a Promise-based API.
 */

import { PLAYGROUND_ORIGIN } from './playground-blueprint';

interface PendingRequest {
  resolve: (data: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const REQUEST_TIMEOUT_MS = 10_000;
const CONNECT_TIMEOUT_MS = 15_000;

export class OpfsManager {
  private iframe: HTMLIFrameElement | null = null;
  private pending = new Map<string, PendingRequest>();
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private connected = false;

  /**
   * Create hidden iframe to PLAYGROUND_ORIGIN/opfs-manager.html.
   * Resolves when the bridge page signals ready.
   */
  async connect(timeoutMs = CONNECT_TIMEOUT_MS): Promise<void> {
    if (this.connected) return;

    return new Promise<void>((resolve, reject) => {
      // Create hidden iframe first so message handlers can verify the exact
      // sender. Multiple probes can overlap during boot; origin alone is not
      // enough to distinguish a stale manager iframe from this instance.
      this.iframe = document.createElement('iframe');
      this.iframe.style.display = 'none';
      this.iframe.src = `${PLAYGROUND_ORIGIN}/opfs-manager.html?t=${Date.now()}`;

      // Listen for the ready signal from the bridge
      const readyHandler = (event: MessageEvent) => {
        if (event.origin !== PLAYGROUND_ORIGIN) return;
        if (event.source !== this.iframe?.contentWindow) return;
        if (event.data?.type === 'opfs-manager-ready') {
          window.removeEventListener('message', readyHandler);
          clearTimeout(timeout);
          this.connected = true;
          resolve();
        }
      };

      const timeout = setTimeout(() => {
        window.removeEventListener('message', readyHandler);
        this.cleanup();
        reject(new Error(`OpfsManager: connection timed out after ${timeoutMs}ms`));
      }, timeoutMs);

      window.addEventListener('message', readyHandler);

      // Set up persistent handler for responses
      this.messageHandler = (event: MessageEvent) => {
        if (event.origin !== PLAYGROUND_ORIGIN) return;
        if (event.source !== this.iframe?.contentWindow) return;
        const data = event.data;
        if (data?.type !== 'opfs-response' || !data.requestId) return;

        const pending = this.pending.get(data.requestId);
        if (!pending) return;

        clearTimeout(pending.timer);
        this.pending.delete(data.requestId);

        if (data.success) {
          pending.resolve(data.data);
        } else {
          pending.reject(new Error(data.error || 'Unknown OPFS error'));
        }
      };
      window.addEventListener('message', this.messageHandler);

      this.iframe.onerror = () => {
        clearTimeout(timeout);
        window.removeEventListener('message', readyHandler);
        this.cleanup();
        reject(new Error('OpfsManager: iframe failed to load'));
      };

      document.body.appendChild(this.iframe);
    });
  }

  /** Remove the iframe and clean up all listeners */
  disconnect(): void {
    for (const [, pending] of this.pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error('OpfsManager: disconnected'));
    }
    this.pending.clear();
    this.cleanup();
  }

  /** List all site IDs stored in OPFS */
  async listSites(): Promise<string[]> {
    return this.sendCommand('list-sites') as Promise<string[]>;
  }

  /** Delete a site's OPFS data */
  async deleteSite(siteId: string): Promise<void> {
    await this.sendCommand('delete-site', { siteId });
  }

  /** Remove the legacy 'sites' root directory from OPFS */
  async cleanupLegacy(): Promise<{ removed: boolean }> {
    return this.sendCommand('cleanup-legacy') as Promise<{ removed: boolean }>;
  }

  /** Get storage usage estimates from the Playground origin */
  async getUsage(): Promise<{ usage: number; quota: number }> {
    return this.sendCommand('get-usage') as Promise<{
      usage: number;
      quota: number;
    }>;
  }

  private sendCommand(
    command: string,
    payload: Record<string, unknown> = {},
    timeoutMs = REQUEST_TIMEOUT_MS,
  ): Promise<unknown> {
    if (!this.connected || !this.iframe?.contentWindow) {
      return Promise.reject(new Error('OpfsManager: not connected'));
    }

    const requestId = `${command}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error(`OpfsManager: ${command} timed out after ${timeoutMs}ms`));
      }, timeoutMs);

      this.pending.set(requestId, { resolve, reject, timer });

      this.iframe!.contentWindow!.postMessage(
        { type: 'opfs-request', requestId, command, ...payload },
        PLAYGROUND_ORIGIN,
      );
    });
  }

  private cleanup(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    if (this.iframe) {
      this.iframe.remove();
      this.iframe = null;
    }
    this.connected = false;
  }
}
